/** Naam en afkorting van een verdieping afleiden uit een bestandsnaam (prototype `parseFloorName`). */
export function parseFloorName(filename: string): { name: string; level: string } {
  const base = filename.replace(/\.[a-z0-9]+$/i, "");
  const s =
    " " +
    base
      .toLowerCase()
      .replace(/[_\-.]+/g, " ")
      .replace(/\s+/g, " ") +
    " ";
  let m: RegExpMatchArray | null;
  const low = " " + base.toLowerCase().replace(/[_.]+/g, " ") + " ";
  if ((m = s.match(/\b(?:kelder|souterrain|parkeerkelder)\s*(\d)\b/)) || (m = low.match(/\s(?:k|-|min )([2-9])\s/))) {
    const n = parseInt(m[1] ?? "", 10);
    if (n >= 2) return { name: `Kelder ${n}`, level: `-${n}` };
  }
  if (/\b(entresol|mezzanine|tussenverdieping)\b/.test(s)) return { name: "Entresol", level: "E" };
  if (
    /\b(kelder|souterrain|parkeerkelder|ondergronds)\b/.test(s) ||
    /\s(k1|-1|min 1)\s/.test(" " + base.toLowerCase() + " ")
  )
    return { name: "Kelder", level: "-1" };
  if (/\b(begane ?grond|bg|parterre|gelijkvloers|ground ?floor)\b/.test(s) || /\s(00|0e|0)\s/.test(s))
    return { name: "Begane grond", level: "BG" };
  if (/\b(dak|dakaanzicht|dakvloer|roof)\b/.test(s)) return { name: "Dak", level: "D" };
  if (
    (m = s.match(/\b(\d{1,2})\s*(e|ste|de)?\s*(verdieping|verd|etage|vd|v|fl|floor|og)\b/)) ||
    (m = s.match(/\b(verdieping|verd|etage|vd|v|fl|floor|og)\s*(\d{1,2})\b/))
  ) {
    const n = parseInt((/\d/.test(m[1] ?? "") ? m[1] : m[2]) ?? "", 10);
    return n === 0 ? { name: "Begane grond", level: "BG" } : { name: `${n}e verdieping`, level: String(n) };
  }
  if ((m = s.match(/\b(\d{1,2})e\b/))) {
    const n = parseInt(m[1] ?? "", 10);
    return { name: `${n}e verdieping`, level: String(n) };
  }
  const nums = [...s.matchAll(/\b(\d{1,2})\b/g)].map((x) => parseInt(x[1] ?? "", 10)).filter((n) => n <= 40);
  if (nums.length === 1) {
    const n = nums[0]!;
    return n === 0 ? { name: "Begane grond", level: "BG" } : { name: `${n}e verdieping`, level: String(n) };
  }
  return { name: base.replace(/[_]+/g, " ").trim() || "Verdieping", level: "" };
}

/** Waarde voor een afkorting of naam die niet als verdieping te herkennen is: achteraan. */
const UNKNOWN_LEVEL = 1000;

/**
 * Sorteerwaarde van een verdiepingsafkorting: kelder 2 → kelder 1 → BG → entresol → 1… → dak
 * (uitbreiding van prototype `levelNum`). Onbekend → 1000.
 */
export function levelNum(level: string | null | undefined): number {
  const l = String(level || "")
    .trim()
    .toUpperCase();
  if (l === "BG" || l === "0") return 0;
  if (l === "E" || l === "ENTRESOL" || l === "MEZZANINE" || l === "TV") return 0.5;
  if (l === "K" || l === "KELDER" || l === "S" || l === "SOUTERRAIN") return -1;
  if (l === "D" || l === "DAK") return 99;
  const k = l.match(/^(?:K|KELDER|S)\s*(\d{1,2})$/);
  if (k) return -parseInt(k[1]!, 10);
  if (!/^-?\d+(?:[.,]\d+)?$/.test(l)) return UNKNOWN_LEVEL;
  return parseFloat(l.replace(",", "."));
}

/** Sorteerwaarde uit de naam, voor verdiepingen zonder (herkenbare) afkorting: "Kelder 2", "3e verdieping". */
function nameNum(name: string): number {
  const s = name.toLowerCase();
  let m: RegExpMatchArray | null;
  if ((m = s.match(/\b(?:kelder|souterrain|parkeerkelder)\s*(\d{1,2})\b/))) return -parseInt(m[1]!, 10);
  if (/\b(kelder|souterrain|parkeerkelder)\b/.test(s)) return -1;
  if (/\b(begane ?grond|parterre|gelijkvloers)\b/.test(s)) return 0;
  if (/\b(entresol|mezzanine|tussenverdieping)\b/.test(s)) return 0.5;
  if (/\b(dak|dakvloer)\b/.test(s)) return 99;
  if ((m = s.match(/\b(\d{1,2})\s*(?:e|ste|de)?\s*(?:verdieping|etage)\b/))) return parseInt(m[1]!, 10);
  return UNKNOWN_LEVEL;
}

/** Sorteerwaarde van een verdieping: eerst de afkorting, anders de naam. */
export function floorRank(f: { level?: string | null; name?: string | null }): number {
  const n = levelNum(f.level);
  return n !== UNKNOWN_LEVEL ? n : nameNum(String(f.name || ""));
}

/**
 * Verdiepingen in logische volgorde (laag → hoog). Gelijke of onherkenbare niveaus houden hun
 * huidige volgorde, zodat handmatig geschoven verdiepingen niet door elkaar gaan.
 */
export function sortFloorsByLevel<T extends { level?: string | null; name?: string | null }>(list: readonly T[]): T[] {
  return list
    .map((f, i) => ({ f, i, r: floorRank(f) }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map((x) => x.f);
}

/** Plek voor een nieuwe of gewijzigde verdieping in een bestaande volgorde: vóór de eerste die hoger ligt. */
export function floorInsertIndex(
  list: readonly { level?: string | null; name?: string | null }[],
  f: { level?: string | null; name?: string | null },
): number {
  const r = floorRank(f);
  const i = list.findIndex((x) => floorRank(x) > r);
  return i < 0 ? list.length : i;
}

/** Naamvoorstel bij ruimtes intekenen: laatste getal ophogen ("Kantoor 2.04" → "Kantoor 2.05"). */
export function suggestName(last: string | null | undefined): string {
  if (!last) return "";
  const m = last.match(/^(.*?)(\d+)(\D*)$/);
  if (!m) return "";
  const n = String(parseInt(m[2]!, 10) + 1).padStart(m[2]!.length, "0");
  return m[1]! + n + m[3]!;
}
